import React, { useState } from 'react';
import { Modal } from '../../../../components';
import style from './add-text.module.scss';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import CrossIcon from '../../../../../../../../assets/svg/solo-chat/circle-cross-4.svg';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { ContentMode } from '../../../../types';
import { errorMessage } from '../../../../utils/error';

type TextMessage = {
	text: string;
	alternates: string[];
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: TextMessage & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean
};

export function AddText(props: Props) {
	const { ErrorToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const { isOpen, close, initialValue, refetchContent, onCreate, isPreview = false } = props;
	const [state, setState] = useState<TextMessage>(() => {
		return {
			text: initialValue?.text ?? '',
			alternates: initialValue?.alternates ?? [],
		}
	});
	const [mode, setMode] = useState(props.mode ?? 'create');

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState<{
		text: string;
		alternates: Record<number, string>;
	}>({
		text: '',
		alternates: {},
	});

	function validateAlternates(error: Record<string, any>) {
		state.alternates.forEach((val, i) => {
			if (!val.trim().length) {
				if (!error.alternates) {
					error.alternates = {};
				}
				error.alternates[i] = "Alternate value can't be empty";
			}
		});
		return Object.keys(error.alternates ?? []).length;
	}

	function addNewAlternate() {
		const error: Record<string, any> = {};
		const errorLen = validateAlternates(error);
		if (errorLen) {
			setError((prev) => ({ ...prev, alternates: error.alternates }));
		} else {
			setState((prev) => ({
				...prev,
				alternates: [...prev.alternates, ''],
			}));
		}
	}

	function validateMessage() {
		const error: Record<string, any> = {};
		if (!state.text.trim().length) {
			error.text = "Message value can't be empty";
		}
		validateAlternates(error);
		const errorLen = Object.keys(error).length;
		if (errorLen) {
			setError(error as any);
		}
		return errorLen === 0;
	}

	return (
		<Modal
			hideFooter={isPreview}
			loading={createLoading || updateLoading}
			isOpen={isOpen}
			styles={{ '--modal-footer-mt': '0px' }}
			title={
				mode === 'edit'
					? 'Update Text'
					: mode === 'create'
					? 'Add Text'
					: 'View Text'
			}
			okText={mode === 'view' ? 'Edit' : mode === 'create' ?  'Submit' : 'Update'}
			onCancel={() => {
				setState({
					text: '',
					alternates: [],
				});
				close();
			}}
			onOk={async () => {
				if (mode !== 'view' && !validateMessage()) {
					return;
				}

				const payload = {
					branchId,
					contentType: 'text',
					contentValue: {
						text: state.text.trim(),
						alternates: state.alternates.map((el) => el.trim()),
					},
				};

				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
					ErrorToast(errorMessage(mode, 'text', err.code));
				}
			}}
		>
			<div className={style.addText}>
				<p className={style.desc}>
					A regular text message with optional and alternates
				</p>

				<div className={style.form}>
					<label htmlFor="message">Message</label>
					<textarea
						disabled={mode === 'view'}
						id="message"
						value={state.text}
						autoFocus
						onChange={(e) => {
							const text = e.target.value;
							if (text.trim() === '') {
								setError((prev) => ({
									...prev,
									text: "Message value can't be empty",
								}));
							} else {
								setError((prev) => ({ ...prev, text: '' }));
							}
							setState((prev) => ({ ...prev, text }));
						}}
					/>
					{error.text && <span className={style.error}>{error.text}</span>}
				</div>

				<div className={style.alternates}>
					<div className={style.addAlternates}>
						{(mode !== 'view' || state.alternates.length > 0) && (
							<p>Alternates (Optional)</p>
						)}
						{mode !== 'view' && (
							<>
								<div className={style.line} />
								<button className={style.add} onClick={addNewAlternate}>
									<img src={AddIcon} alt="add" />
									<span>Add Alternates</span>
								</button>
							</>
						)}
					</div>

					<div className={style.list}>
						{state.alternates.map((el, elI) => {
							return (
								<div key={elI} className={style.alternate}>
									<div style={{ flex: 1 }}>
										<input
											disabled={mode === 'view'}
											autoFocus
											onChange={(e) => {
												const value = e.target.value;
												if (value.trim() !== '') {
													setError((prev) => ({
														...prev,
														alternates: {
															...prev.alternates,
															[elI]: '',
														},
													}));
												} else {
													setError((prev) => ({
														...prev,
														alternates: {
															...prev.alternates,
															[elI]: "Alternate value can't be empty",
														},
													}));
												}
												setState((prev) => ({
													...prev,
													alternates: prev.alternates.map((al, i) =>
														i === elI ? value : al,
													),
												}));
											}}
											value={el}
										/>
										{error.alternates?.[elI] && (
											<span className={style.error}>
												{error.alternates[elI]}
											</span>
										)}
									</div>
									{mode !== 'view' && (
										<button
											onClick={() => {
												setState((prev) => ({
													...prev,
													alternates: prev.alternates.filter(
														(_, i) => i !== elI,
													),
												}));
											}}
										>
											<img src={CrossIcon} alt="delete" />
										</button>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</Modal>
	);
}
